import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";
import { Play, Mail, Phone, Music, Star, ChevronDown, Menu, X } from "lucide-react";

// Admin imports
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminHero from "@/pages/AdminHero";
import AdminAbout from "@/pages/AdminAbout";
import AdminGallery from "@/pages/AdminGallery";
import AdminServices from "@/pages/AdminServices";
import AdminPricing from "@/pages/AdminPricing";
import AdminVideos from "@/pages/AdminVideos";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminNews from "@/pages/AdminNews";
import AdminContacts from "@/pages/AdminContacts";
import AdminContactSection from "@/pages/AdminContactSection";
import AdminSEO from "@/pages/AdminSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Scroll Animation Hook
const useScrollAnimation = () => {
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in-view');
            }
          });
        },
        { threshold: 0.1, rootMargin: '50px 0px -20px 0px' }
      );

      const elements = document.querySelectorAll('.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale');
      elements.forEach((el) => observer.observe(el));

      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timer);
  }, []);
};

// Smooth scroll function with slower duration
const smoothScrollTo = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    const targetPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = 1200; // 1.2 seconds for slower scroll
    let start = null;

    const animation = (currentTime) => {
      if (start === null) start = currentTime;
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function for smooth deceleration
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      window.scrollTo(0, startPosition + distance * easeOutCubic);
      
      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }
};

// Image URLs
const IMAGES = {
  heroHeadshot: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/982utd4e_KERRY%20WINTER-2%202.jpg",
  aboutHeadshot: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/fbk0hfzq_KERRY%20WINTER-5.jpg",
  servicesHeadshot: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/n1l32rso_KERRY%20WINTER-7.jpg",
  showTinkerbell: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/b7wyo0zq_10559732_10152621095026113_5928298957843180288_n.jpg",
  showGrease: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/qyk8wrej_Jan.jpg",
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "about", label: "About" },
    { href: "services", label: "Services" },
    { href: "pricing", label: "Pricing" },
    { href: "videos", label: "Watch" },
    { href: "testimonials", label: "Testimonials" },
    { href: "news", label: "News" },
    { href: "contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-header shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="font-serif text-2xl md:text-3xl tracking-tight" data-testid="logo-link">
            The Sing Studio
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => smoothScrollTo(link.href)}
                className="nav-link text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 right-0 bg-background border-b border-border py-6 px-6 animate-fade-in">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => {
                  smoothScrollTo(link.href);
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground bg-transparent border-none cursor-pointer"
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section data-testid="hero-section" className="min-h-screen flex items-center relative grain-overlay pt-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Left Content */}
          <div className="order-2 md:order-1">
            <p className={`text-sm tracking-widest uppercase text-accent-gold mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.1s' }}>
              West End Trained Vocal Coach
            </p>
            <h1 className={`text-5xl md:text-7xl font-serif tracking-tight leading-none mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.2s' }}>
              Discover Your
              <span className="block italic text-accent-gold">True Voice</span>
            </h1>
            <p className={`text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 max-w-lg transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.3s' }}>
              Professional singing lessons in Yateley with Kerry Winter. Transform your voice with West End expertise.
            </p>
            <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.4s' }}>
              <Button
                data-testid="hero-cta-button"
                className="btn-shine rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold bg-primary text-primary-foreground hover:bg-accent hover:text-white transition-all duration-300"
                onClick={() => smoothScrollTo('contact')}
              >
                Book a Lesson
              </Button>
              <Button
                data-testid="hero-secondary-button"
                variant="outline"
                className="rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                onClick={() => smoothScrollTo('about')}
              >
                Meet Kerry
              </Button>
            </div>
          </div>

          {/* Right Image */}
          <div className="order-1 md:order-2 flex justify-center md:justify-end">
            <div className={`hero-image-frame relative transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '0.3s' }}>
              <img
                src={IMAGES.heroHeadshot}
                alt="Kerry Winter - Vocal Coach"
                className="w-full max-w-md object-cover max-h-[350px] md:max-h-none"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-accent-gold" size={32} />
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const credits = [
    "The Sound of Music - London Palladium",
    "Grease - Piccadilly Theatre",
    "Shrek The Musical - Theatre Royal Drury Lane",
    "The Rocky Horror Show - European Tour",
    "Josh Groban UK Tour - O2 Arena",
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-muted relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Image Column */}
          <div className="md:col-span-5">
            <div className="relative">
              <img
                src={IMAGES.aboutHeadshot}
                alt="Kerry Winter"
                className="w-full object-cover"
                data-testid="about-image"
              />
              <div className="absolute bottom-4 right-0 md:bottom-6 md:-right-4 bg-accent text-white px-4 md:px-6 py-2 md:py-3">
                <span className="text-xs md:text-sm tracking-widest uppercase font-semibold">West End Performer</span>
              </div>
            </div>
          </div>

          {/* Content Column */}
          <div className="md:col-span-7 md:pl-8">
            <p className="scroll-animate text-sm tracking-widest uppercase text-accent-gold mb-4">About Kerry</p>
            <h2 className="scroll-animate stagger-1 text-4xl md:text-5xl font-serif tracking-tight mb-8">
              From West End Stage to Your Singing Journey
            </h2>
            <div className="scroll-animate stagger-2 section-divider mb-8"></div>
            
            <div className="scroll-animate stagger-3 space-y-6 text-muted-foreground leading-relaxed">
              <p>
                Kerry trained at Mountview Academy of Theatre Arts, graduating with a First Class Honours degree in Musical Theatre and Performance.
              </p>
              <p>
                Her professional career began at the renowned Menier Chocolate Factory in The Last Five Years, before joining the original cast of The Sound of Music at the London Palladium, working alongside Sir Andrew Lloyd Webber and fulfilling her childhood dream of performing as Maria Von Trapp.
              </p>
              <p>
                Kerry's extensive West End experience includes playing Jan in Grease at the Piccadilly Theatre, and voicing the spectacular Dragon puppet in Shrek The Musical at Theatre Royal Drury Lane. She has performed at London's O2 Arena as a backing singer for Josh Groban's UK Tour.
              </p>
            </div>

            {/* Credits */}
            <div className="scroll-animate stagger-4 mt-10">
              <p className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Selected Credits</p>
              <div className="flex flex-wrap gap-2">
                {credits.map((credit) => (
                  <span key={credit} className="credit-badge">
                    {credit}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Performance Gallery Section
const PerformanceGallery = () => {
  const images = [
    {
      src: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/71uc6bxq_10559732_10152621095026113_5928298957843180288_n.jpg",
      alt: "Kerry as Tinkerbell in Peter Pan",
      caption: "Tinkerbell",
      rotation: "-rotate-3",
    },
    {
      src: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/yf98fcrk_Jan.jpg",
      alt: "Kerry as Jan in Grease",
      caption: "Jan - Grease",
      rotation: "rotate-2",
    },
    {
      src: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/hzgca8j3_Kerry.jpg",
      alt: "Kerry as Dragon in Shrek The Musical",
      caption: "Shrek",
      rotation: "-rotate-2",
    },
    {
      src: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/3po6cpor_Rocky.jpg",
      alt: "Kerry as Columbia in Rocky Horror Show",
      caption: "Rocky Horror",
      rotation: "rotate-3",
    },
  ];

  return (
    <section data-testid="performance-gallery" className="py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="scroll-animate text-sm tracking-widest uppercase text-accent-gold mb-4">On Stage</p>
          <h2 className="scroll-animate stagger-1 text-3xl md:text-4xl font-serif tracking-tight">West End & Beyond</h2>
        </div>
        
        {/* Polaroid Montage */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8">
          {images.map((image, index) => (
            <div
              key={index}
              className={`scroll-animate-scale stagger-${index + 1} group ${image.rotation} hover:rotate-0 transition-all duration-300 hover:scale-105 hover:z-10`}
              style={{ marginTop: index % 2 === 0 ? '0' : '1.5rem' }}
              data-testid={`gallery-image-${index}`}
            >
              {/* Polaroid Frame */}
              <div className="relative bg-white p-2 md:p-3 shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.2)] transition-shadow duration-300">
                <div className="w-36 h-44 md:w-56 md:h-64 overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="h-10 md:h-12 flex items-center justify-center">
                  <p className="text-gray-600 text-center" style={{ fontFamily: "'Caveat', cursive", fontSize: '1.1rem' }}>
                    {image.caption}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="scroll-animate text-sm tracking-widest uppercase text-accent-gold mb-4">What I Offer</p>
          <h2 className="scroll-animate stagger-1 text-4xl md:text-5xl font-serif tracking-tight">Tailored Vocal Training</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Private Lessons Card */}
          <div 
            className="scroll-animate-left group bg-primary text-primary-foreground p-10 md:p-14 hover-lift"
            data-testid="private-lessons-card"
          >
            <div className="relative z-10">
              <Music className="text-accent mb-6" size={40} />
              <h3 className="text-2xl md:text-3xl font-serif mb-4">Private Lessons</h3>
              <p className="text-primary-foreground/80 leading-relaxed mb-6">
                One-to-one vocal tuition in a fun and friendly environment. Whether you're a complete beginner or established performer, Kerry will transform your singing voice.
              </p>
              <ul className="space-y-3 text-sm text-primary-foreground/70">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                  Breath technique & control
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                  Range development & safe belting
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                  All genres: Musical Theatre, Pop, Jazz, Classical
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                  Audition preparation
                </li>
              </ul>
              <p className="mt-6 text-sm text-primary-foreground/60">
                Based in Yateley, serving Fleet, Farnborough, Crowthorne & surrounding areas
              </p>
            </div>
          </div>

          {/* School Teaching Card */}
          <div 
            className="scroll-animate-right bg-secondary p-10 md:p-14 hover-lift"
            data-testid="school-teaching-card"
          >
            <Star className="text-accent mb-6" size={40} />
            <h3 className="text-2xl md:text-3xl font-serif mb-4">School Teaching</h3>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Kerry brings her professional expertise to prestigious educational settings, nurturing the next generation of performers.
            </p>
            <div className="bg-background p-6 border border-border">
              <div className="flex items-center gap-6">
                <p className="text-sm text-muted-foreground flex-1">
                  Delivering exceptional vocal training to students at this distinguished independent school.
                </p>
                <img 
                  src="https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/o2hv29v4_LVS-Ascot.png" 
                  alt="LVS Ascot Logo"
                  className="h-16 md:h-20 w-auto flex-shrink-0"
                />
              </div>
            </div>
            <div className="mt-8 space-y-3 text-sm text-muted-foreground">
              <p>Kerry's teaching approach includes:</p>
              <ul className="space-y-2 ml-4">
                <li>• Musical theatre masterclasses</li>
                <li>• Individual vocal development</li>
                <li>• Performance skills & confidence building</li>
                <li>• Exam preparation (all ages 3-78!)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Method Section */}
        <div className="scroll-animate mt-16 bg-muted p-10 md:p-14">
          <div className="max-w-3xl">
            <h3 className="text-2xl md:text-3xl font-serif mb-6">The Method</h3>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Kerry has developed a passion for teaching through her performing career. Her classes are fun, creative and full of information.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Working mainly without a piano except for warm-ups, Kerry gives her full focus to each student, working far closer than a tutor concentrating on playing the piano. Whether it's a masterclass for 30 children or a one-on-one with a beginner, Kerry is at home when teaching singing.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const pricingOptions = [
    {
      title: "Under 12's",
      duration: "45 minutes",
      price: "£35",
      features: ["Full warm-up routine", "Technique development", "Song work", "Sheet music & backing tracks included"],
    },
    {
      title: "Over 12's",
      duration: "1 hour",
      price: "£45",
      features: ["Comprehensive warm-up", "Advanced technique work", "Performance coaching", "Audition preparation", "Demo recording each term"],
    },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-muted">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="scroll-animate text-sm tracking-widest uppercase text-accent-gold mb-4">Investment</p>
          <h2 className="scroll-animate stagger-1 text-4xl md:text-5xl font-serif tracking-tight mb-6">Lesson Pricing</h2>
          <p className="scroll-animate stagger-2 text-muted-foreground max-w-2xl mx-auto">
            Kerry believes everyone CAN sing and can be developed to vocal excellence. Whether you sing for profit or pleasure, it's never too late to start.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {pricingOptions.map((option, index) => (
            <div
              key={option.title}
              className={`scroll-animate-scale stagger-${index + 2} ticket-card p-8 md:p-10 hover-lift`}
              data-testid={`pricing-card-${index}`}
            >
              <div className="ticket-dashed hidden md:block"></div>
              <div className="md:pl-8">
                <p className="text-sm tracking-widest uppercase text-accent-gold mb-2">{option.title}</p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-serif">{option.price}</span>
                </div>
                <p className="text-muted-foreground mb-6">{option.duration}</p>
                <div className="section-divider mb-6"></div>
                <ul className="space-y-3 mb-8">
                  {option.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`enquire-btn-${index}`}
                  className="w-full btn-shine rounded-full px-6 py-5 text-sm uppercase tracking-widest font-semibold bg-primary text-primary-foreground hover:bg-accent hover:text-white transition-all duration-300"
                  onClick={() => smoothScrollTo('contact')}
                >
                  Enquire Now
                </Button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-10">
          Students will be provided with a Sing Studio Music folder, all Sheet Music, Lyrics and Backing Tracks.
        </p>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => {
  const [playingVideo, setPlayingVideo] = useState(null);

  const videos = [
    {
      id: "pfi6pl4u_4w",
      title: "Never Enough",
      subtitle: "The Greatest Showman",
      extraInfo: null,
    },
    {
      id: "uwf4PhuOqsQ",
      title: "Forever",
      subtitle: "Shrek The Musical",
      extraInfo: "West End Production of Shrek",
    },
  ];

  return (
    <section id="videos" data-testid="videos-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="scroll-animate text-sm tracking-widest uppercase text-accent-gold mb-4">Listen</p>
          <h2 className="scroll-animate stagger-1 text-4xl md:text-5xl font-serif tracking-tight">Hear Kerry Sing</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {videos.map((video, index) => (
            <div key={video.id} className={`scroll-animate-scale stagger-${index + 1} space-y-4`} data-testid={`video-container-${video.id}`}>
              <div className="video-container aspect-video relative">
                {playingVideo === video.id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img
                      src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                      }}
                    />
                    <div
                      className="video-overlay"
                      onClick={() => setPlayingVideo(video.id)}
                      data-testid={`video-play-${video.id}`}
                    >
                      <div className="w-20 h-20 rounded-full bg-accent flex items-center justify-center transition-transform duration-300 hover:scale-110">
                        <Play className="text-white ml-1" size={32} fill="white" />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="text-center">
                <h3 className="text-xl font-serif">{video.title}</h3>
                <p className="text-sm text-muted-foreground">{video.subtitle}</p>
                {video.extraInfo && (
                  <p className="text-xs text-accent-gold mt-1 italic">{video.extraInfo}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  // Default testimonials if none from API
  const displayTestimonials = testimonials.length > 0 ? testimonials : [
    {
      id: "1",
      name: "Sarah M.",
      content: "Kerry has transformed my daughter's confidence. After just a few months, she's performing solos at school and loving every minute. The professional training makes all the difference.",
      student_type: "Parent",
    },
    {
      id: "2",
      name: "James T.",
      content: "As an adult beginner, I was nervous about taking lessons. Kerry made me feel at ease immediately. Her West End experience shines through in every session.",
      student_type: "Adult Student",
    },
    {
      id: "3",
      name: "Emma R.",
      content: "The best decision we made for our son. Kerry's approach is fun yet professional, and the progress has been remarkable. Highly recommend The Sing Studio!",
      student_type: "Parent",
    },
  ];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="scroll-animate text-sm tracking-widest uppercase text-accent mb-4">Testimonials</p>
          <h2 className="scroll-animate stagger-1 text-4xl md:text-5xl font-serif tracking-tight">What Students Say</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {displayTestimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`scroll-animate stagger-${index + 1} bg-primary-foreground/5 backdrop-blur-sm p-8 border border-primary-foreground/10`}
              data-testid={`testimonial-${index}`}
            >
              <span className="quote-mark">"</span>
              <p className="text-primary-foreground/80 leading-relaxed -mt-8 mb-6">
                {testimonial.content}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white font-semibold">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-primary-foreground/60">{testimonial.student_type}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// News Section
const NewsSection = () => {
  const newsItems = [
    {
      id: 1,
      title: "Made In Dagenham",
      subtitle: "Anniversary Concert at The London Palladium",
      description: "Kerry plays Claire in \"Made In Dagenham\" alongside Pixie Lott and Bonnie Langford in this spectacular anniversary concert at The London Palladium!",
      image: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/q6t9zeuz_Made-in-Dagenham-concert.jpg",
      links: null,
    },
    {
      id: 2,
      title: "Sing-A-Saurus",
      subtitle: "YouTube Kids Series",
      description: "Kerry voices Roxy Roar in the popular YouTube Kids series Sing-A-Saurus - fun, educational songs for children!",
      image: "https://customer-assets.emergentagent.com/job_8551e215-8085-4d52-b0f6-164838a3541d/artifacts/nq5c3fh8_IMG_1435.PNG",
      links: [
        { label: "Listen on Spotify", url: "https://open.spotify.com/track/0qeOzjemigr3LmHpSbF3IC?si=ff1e455428a2447c", icon: "spotify" },
        { label: "Watch on YouTube", url: "https://www.youtube.com/watch?v=F3odMuUaWVA", icon: "youtube" },
      ],
    },
  ];

  return (
    <section id="news" data-testid="news-section" className="py-24 md:py-32 bg-muted">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="scroll-animate text-sm tracking-widest uppercase text-accent-gold mb-4">Latest News</p>
          <h2 className="scroll-animate stagger-1 text-4xl md:text-5xl font-serif tracking-tight">What's Happening</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {newsItems.map((item, index) => (
            <div
              key={item.id}
              className={`scroll-animate-scale stagger-${index + 1} bg-white border border-border overflow-hidden hover-lift group`}
              data-testid={`news-item-${item.id}`}
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <p className="text-sm tracking-widest uppercase text-accent-gold mb-2">{item.subtitle}</p>
                <h3 className="text-2xl font-serif mb-4">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">{item.description}</p>
                {item.links && (
                  <div className="flex flex-wrap gap-4">
                    {item.links.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent transition-colors"
                        data-testid={`news-link-${link.icon}`}
                      >
                        {link.icon === "spotify" && (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                          </svg>
                        )}
                        {link.icon === "youtube" && (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                        )}
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    student_age: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! Kerry will be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "", student_age: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          {/* Left Content */}
          <div>
            <p className="scroll-animate text-sm tracking-widest uppercase text-accent-gold mb-4">Get In Touch</p>
            <h2 className="scroll-animate stagger-1 text-4xl md:text-5xl font-serif tracking-tight mb-8">
              Ready to Start Your Singing Journey?
            </h2>
            <div className="scroll-animate stagger-2 section-divider mb-8"></div>
            <p className="scroll-animate stagger-3 text-muted-foreground leading-relaxed mb-10">
              Contact Kerry to discuss your goals and book your first lesson. All ages and abilities welcome – it's never too late to discover your voice.
            </p>

            <div className="scroll-animate stagger-4 space-y-6">
              <a
                href="mailto:kerry@thesingstudio.com"
                className="flex items-center gap-4 text-foreground hover:text-accent transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 border border-border flex items-center justify-center group-hover:border-accent group-hover:bg-accent transition-all">
                  <Mail size={20} className="group-hover:text-white" />
                </div>
                <span>kerry@thesingstudio.com</span>
              </a>
            </div>

            {/* Kerry's Image & Sign-off */}
            <div className="scroll-animate stagger-5 mt-12 flex items-end gap-6">
              <div className="relative -rotate-2 hover:rotate-0 transition-transform duration-300">
                <div className="bg-white p-2 shadow-lg">
                  <img
                    src={IMAGES.servicesHeadshot}
                    alt="Kerry Winter"
                    className="w-36 h-44 object-cover"
                  />
                </div>
              </div>
              <div className="pb-4">
                <p className="text-lg text-muted-foreground italic mb-2" style={{ fontFamily: "'Caveat', cursive", fontSize: '1.5rem' }}>
                  I can't wait to hear you sing!
                </p>
                <p className="text-accent-gold" style={{ fontFamily: "'Caveat', cursive", fontSize: '2rem' }}>
                  Kerry x
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="scroll-animate-right bg-muted p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-underline w-full"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-underline w-full"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number (Optional)"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-underline w-full"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <select
                  name="student_age"
                  value={formData.student_age}
                  onChange={handleChange}
                  className="input-underline w-full bg-transparent"
                  data-testid="contact-age-select"
                >
                  <option value="">Student Age Group</option>
                  <option value="under-12">Under 12</option>
                  <option value="12-18">12-18</option>
                  <option value="adult">Adult</option>
                </select>
              </div>
              <div>
                <textarea
                  name="message"
                  placeholder="Tell me about your singing goals..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="input-underline w-full resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="btn-shine w-full rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold bg-primary text-primary-foreground hover:bg-accent hover:text-white transition-all duration-300"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-16 bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center">
          <h3 className="text-3xl md:text-4xl font-serif mb-4">The Sing Studio</h3>
          <p className="text-primary-foreground/60 mb-8">Professional Singing Lessons in Yateley</p>
          
          <div className="flex justify-center gap-8 mb-8 text-sm">
            <a href="#about" className="text-primary-foreground/60 hover:text-accent transition-colors">About</a>
            <a href="#services" className="text-primary-foreground/60 hover:text-accent transition-colors">Services</a>
            <a href="#pricing" className="text-primary-foreground/60 hover:text-accent transition-colors">Pricing</a>
            <a href="#contact" className="text-primary-foreground/60 hover:text-accent transition-colors">Contact</a>
          </div>

          <a
            href="mailto:kerry@thesingstudio.com"
            className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
          >
            <Mail size={18} />
            kerry@thesingstudio.com
          </a>

          <div className="mt-12 pt-8 border-t border-primary-foreground/10 text-sm text-primary-foreground/40">
            <p>© {new Date().getFullYear()} The Sing Studio. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// SEO Hook - fetches and applies SEO settings
const useSEO = () => {
  useEffect(() => {
    const fetchSEO = async () => {
      try {
        const response = await axios.get(`${API}/content/seo`);
        const { title, description } = response.data;
        
        // Set page title
        if (title) {
          document.title = title;
        }
        
        // Set meta description
        if (description) {
          let metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.name = 'description';
            document.head.appendChild(metaDesc);
          }
          metaDesc.content = description;
        }
      } catch (error) {
        console.error('Error fetching SEO settings:', error);
      }
    };
    
    fetchSEO();
  }, []);
};

// Home Page Component
const HomePage = () => {
  useScrollAnimation();
  useSEO();
  
  return (
    <>
      <Navigation />
      <HeroSection />
      <AboutSection />
      <PerformanceGallery />
      <ServicesSection />
      <PricingSection />
      <VideoSection />
      <TestimonialsSection />
      <NewsSection />
      <ContactSection />
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Website */}
          <Route path="/" element={<HomePage />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="hero" element={<AdminHero />} />
            <Route path="about" element={<AdminAbout />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="videos" element={<AdminVideos />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="contact-section" element={<AdminContactSection />} />
            <Route path="seo" element={<AdminSEO />} />
          </Route>
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
